import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ProjectCapacitySummary } from '../../services/api/capacity';
import { formatFte } from '../../utils/format';
import ProjectCapacityGrid from './ProjectCapacityGrid';

interface ProjectsCapacitySummaryProps {
  projects: ProjectCapacitySummary[];
  months?: string[];
}

function monthLabel(month: string): string {
  const [year, m] = month.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(m!, 10) - 1]} ${year!.slice(2)}`;
}

function statusDot(status: string): string {
  if (status === 'full') return 'bg-green-500';
  if (status === 'partial') return 'bg-amber-500';
  return 'bg-red-400';
}

export default function ProjectsCapacitySummary({ projects, months: passedMonths }: ProjectsCapacitySummaryProps) {
  const [expandedProjectId, setExpandedProjectId] = useState<number | null>(null);

  if (projects.length === 0) return null;

  // Use passed months (from planning matrix) for column alignment, fallback to derived
  const months = passedMonths ?? (() => {
    const allMonths = new Set<string>();
    projects.forEach((p) => p.months.forEach((m) => allMonths.add(m.month)));
    return [...allMonths].sort();
  })();

  // First column width = Person(180) + Roles(80) + Util(50) = 310px to align with matrix
  return (
    <div className="mt-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Project Capacity Overview</h3>
      <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
        <table className="divide-y divide-gray-200 text-xs" style={{ tableLayout: 'fixed' }}>
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-500 uppercase w-[310px]">Project</th>
              {months.map((m) => (
                <th key={m} className="px-2 py-2 text-center font-medium text-gray-500 uppercase whitespace-nowrap w-[120px]">
                  {monthLabel(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {projects.map((proj) => {
              const monthMap = new Map(proj.months.map((m) => [m.month, m]));
              const isExpanded = expandedProjectId === proj.projectId;
              return (
                <tr key={proj.projectId} className="group">
                  <td colSpan={isExpanded ? months.length + 1 : 1} className={isExpanded ? 'p-0' : 'sticky left-0 z-10 bg-white px-3 py-2 font-medium text-gray-900 whitespace-nowrap'}>
                    {isExpanded ? (
                      <div>
                        <div className="flex items-center gap-2 px-3 py-2">
                          <button
                            onClick={() => setExpandedProjectId(null)}
                            className="p-0.5 text-gray-400 hover:text-gray-600"
                            title="Collapse"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          <Link to={`/projects/${proj.projectId}`} className="text-primary-600 hover:underline font-medium text-sm">
                            {proj.projectName || `Project #${proj.projectId}`}
                          </Link>
                        </div>
                        <ProjectCapacityGrid projectId={proj.projectId} />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setExpandedProjectId(proj.projectId)}
                          className="p-0.5 text-gray-400 hover:text-gray-600"
                          title="Expand capacity detail"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        <Link to={`/projects/${proj.projectId}`} className="text-primary-600 hover:underline">
                          {proj.projectName || `Project #${proj.projectId}`}
                        </Link>
                      </div>
                    )}
                  </td>
                  {!isExpanded && months.map((m) => {
                    const data = monthMap.get(m);
                    if (!data) return <td key={m} className="px-2 py-2 text-center text-gray-300">-</td>;
                    return (
                      <td key={m} className="px-2 py-2 text-center" title={`${formatFte(data.totalAssigned)}/${formatFte(data.totalNeeded)} FTE`}>
                        <div className="flex items-center justify-center gap-1">
                          <span className={`inline-block w-2.5 h-2.5 rounded-full ${statusDot(data.status)}`} />
                          {data.gap > 0 && (
                            <span className="text-[10px] text-red-600 font-medium">-{formatFte(data.gap)}</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
